import {
  FC,
  MutableRefObject,
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { Provider as ReduxProvider } from "react-redux";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import Theme from "./Theme";
import GlobalStyles from "./GlobalStyles";
import useApp, { Props, ReceivedProps } from "./hook";
import store from "./store";
import AppRoutes from "../routes";
import { ConfigProvider } from "antd";
import locale from "antd/es/locale/ja_JP";
import "dayjs/locale/ja";
import { Status } from "@lhd/constants";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

type Confirming = {
  status: string;
  clearOnCancel?: boolean;
} | null;

type ConfirmAction = () => void | Promise<void>;

type ConfirmingContextType = {
  confirmingState: Confirming;
  setConfirmingState: (status: Confirming) => void;
  callbackEvent: ConfirmAction | null | void;
  setCallbackEvent: any;
  checkLeaveForm: () => void;
  needConfirm: boolean;
  setNeedConfirm: (needConfirm: boolean) => void;
  isExecutingConfirmedActionRef: MutableRefObject<boolean>;
  confirmAction: (action: ConfirmAction) => void;
};

const defaultExecutingConfirmedActionRef = { current: false };

const ConfirmingContext = createContext<ConfirmingContextType>({
  confirmingState: null,
  setConfirmingState: () => null,
  callbackEvent: null,
  setCallbackEvent: null,
  checkLeaveForm: () => null,
  needConfirm: false,
  setNeedConfirm: () => null,
  isExecutingConfirmedActionRef: defaultExecutingConfirmedActionRef,
  confirmAction: (action) => void action(),
});

export function useConfirmingLeave(): ConfirmingContextType {
  return useContext(ConfirmingContext);
}

function ConfirmingLeaveProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const [confirmingState, setConfirmingState] = useState<Confirming>(null);
  const [callbackEvent, setCallbackEvent] =
    useState<ConfirmAction | null | void>(null);
  const [needConfirm, setNeedConfirm] = useState(false);
  const isExecutingConfirmedActionRef = useRef(false);

  const checkLeaveForm = useCallback(() => {
    if (!confirmingState) {
      setConfirmingState({
        status: Status.UnSave,
      });
    }
    return;
  }, [confirmingState, setConfirmingState]);

  const confirmAction = useCallback(
    (action: ConfirmAction) => {
      if (
        isExecutingConfirmedActionRef.current ||
        (!needConfirm && !confirmingState)
      ) {
        void action();
        return;
      }

      setCallbackEvent(() => action);
      setConfirmingState({
        status: Status.OpenModal,
        clearOnCancel: true,
      });
    },
    [confirmingState, needConfirm],
  );

  const value = {
    confirmingState,
    setConfirmingState,
    callbackEvent,
    setCallbackEvent,
    checkLeaveForm,
    needConfirm,
    setNeedConfirm,
    isExecutingConfirmedActionRef,
    confirmAction,
  };

  return (
    <ConfirmingContext.Provider value={value}>
      {children}
    </ConfirmingContext.Provider>
  );
}

const AppLayout: FC = () => (
  <ReduxProvider store={store}>
    <ConfigProvider locale={locale}>
      <ConfirmingLeaveProvider>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          <Theme>
            <DndProvider backend={HTML5Backend}>
              <GlobalStyles />
              <AppRoutes />
            </DndProvider>
          </Theme>
        </QueryClientProvider>
      </ConfirmingLeaveProvider>
    </ConfigProvider>
  </ReduxProvider>
);

const App: FC<Props> = (props: ReceivedProps) => (
  <AppLayout {...useApp(props)} />
);

export default App;
